/**
 * AvatarSync Component
 *
 * Handles syncing the current user's avatar from Matrix to LoginContext.
 * This component must be rendered inside MatrixClientProvider to have access to the client.
 * 
 * Refactored to use focused hooks for better maintainability:
 * - useUserAvatar: Handles avatar loading and blob URL management
 * - useMatrixAvatarListener: Handles Matrix avatar change events
 */

import { useEffect, useMemo } from 'react';
import { useLogin } from '../context/LoginContext';
import { useUserAvatar } from '../hooks/useUserAvatar';
import { useMatrixAvatarListener } from '../hooks/useMatrixAvatarListener';
import logger from '../utils/logger';

export const AvatarSync = () => {
  const { currentUser, setCurrentUser } = useLogin();

  // Create a stable userId reference to prevent unnecessary re-renders
  const userId = useMemo(
    () => currentUser?.id || currentUser?.matrixUserId,
    [currentUser?.id, currentUser?.matrixUserId],
  );

  const displayName = currentUser?.name || userId;

  // Load and manage the user's avatar
  // This hook handles:
  // - Fetching avatar with authentication
  // - IndexedDB caching
  // - Blob URL creation and cleanup
  const { avatarUrl, loadAvatar } = useUserAvatar(userId, displayName, 96);

  // Listen for avatar changes from Matrix
  // When the user changes their avatar in another client,
  // this triggers a reload
  useMatrixAvatarListener(userId, loadAvatar);

  // Sync avatar URL to LoginContext
  // Update currentUser when the avatar URL changes
  useEffect(() => {
    if (!avatarUrl) {
      return;
    }

    // Only update if the avatar actually changed and setCurrentUser is available
    // Defensive check: while useLogin() throws if context is missing, this prevents
    // issues in test scenarios or future refactoring
    if (avatarUrl !== currentUser?.avatar && setCurrentUser) {
      logger.debug(`AvatarSync: Updating currentUser avatar: ${avatarUrl.substring(0, 50)}...`);
      setCurrentUser(prev => ({
        ...prev,
        avatar: avatarUrl,
      }));
      logger.info('AvatarSync: User avatar synced to LoginContext');
    }
  }, [avatarUrl, currentUser?.avatar, setCurrentUser]);

  // Render a hidden marker to ensure React executes this component
  return <div data-component="avatar-sync" style={{ display: 'none' }} />;
};

export default AvatarSync;
